import { getApiHeaders } from '../utils/apiHeaders';

const API_BASE_URL = process.env.REACT_APP_TRAVEL_API_BASE_URL;

export type ErrorResponse = { error: string; message?: string; [k: string]: any };

export type ActivitySummary = {
  id: number;
  name: string;
  description: string | null;
  image_path: string | null;
  city_tour_type: string | null;
};

export type ActivityListResponse = {
  items: ActivitySummary[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
  priceJobId?: string;
};

export type ActivityPrice = {
  amount: number;
  discount?: number;
  contractId?: number;
  sortOrder?: number;
  currency?: string;
};

export type ActivityPricePollResponse =
  | { status: 'PENDING' }
  | { status: 'FAILED'; error?: string }
  | { status: 'SUCCESS'; currency: 'INR'; prices: Record<string, ActivityPrice> };

export type ActivityCity = {
  cityId: number;
  cityName: string | null;
  countryId: number;
  countryName: string | null;
};

export type FetchToursParams = {
  page?: number;
  limit?: number;
  pageSize?: number;
  name?: string;
  city?: string;
  cityId?: number;
  country?: string;
  countryId?: number;
  travelDate?: string;
};

const toNumber = (value: unknown, fallback = 0): number => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const toNullableString = (value: unknown): string | null => {
  if (value === undefined || value === null) return null;
  return String(value);
};

const buildError = async (response: Response, fallback: string) => {
  try {
    const body = await response.json();
    const text = body?.message || body?.error;
    if (text) return new Error(String(text));
  } catch {
    // fall through to status-based message
  }
  return new Error(`${fallback} (${response.status} ${response.statusText})`);
};

const normalizeSummary = (item: any): ActivitySummary => ({
  id: toNumber(item?.id ?? item?.tourId ?? item?.tour_id, 0),
  name: String(item?.name || ''),
  description: toNullableString(item?.description),
  image_path: toNullableString(item?.image_path ?? item?.imagePath ?? item?.image_url),
  city_tour_type: toNullableString(item?.city_tour_type ?? item?.cityTourType),
});

const normalizeCity = (item: any): ActivityCity => ({
  cityId: toNumber(item?.cityId ?? item?.city_id, 0),
  cityName: toNullableString(item?.cityName ?? item?.city_name),
  countryId: toNumber(item?.countryId ?? item?.country_id, 0),
  countryName: toNullableString(item?.countryName ?? item?.country_name),
});

const normalizePrice = (price: any): ActivityPrice => ({
  amount: toNumber(price?.amount, 0),
  discount: price?.discount === undefined || price?.discount === null ? undefined : toNumber(price?.discount, 0),
  contractId: price?.contractId === undefined || price?.contractId === null ? undefined : toNumber(price?.contractId, 0),
  sortOrder: price?.sortOrder === undefined || price?.sortOrder === null ? undefined : toNumber(price?.sortOrder, 0),
  currency: price?.currency ? String(price.currency) : undefined,
});

export const fetchTours = async (params: FetchToursParams = {}): Promise<ActivityListResponse> => {
  const searchParams = new URLSearchParams();
  if (params.page !== undefined) searchParams.set('page', String(params.page));
  if (params.limit !== undefined) searchParams.set('limit', String(params.limit));
  if (params.pageSize !== undefined) searchParams.set('pageSize', String(params.pageSize));
  if (params.name) searchParams.set('name', params.name);
  if (params.city) searchParams.set('city', params.city);
  if (params.cityId !== undefined) searchParams.set('cityId', String(params.cityId));
  if (params.country) searchParams.set('country', params.country);
  if (params.countryId !== undefined) searchParams.set('countryId', String(params.countryId));
  if (params.travelDate) searchParams.set('travelDate', params.travelDate);

  const response = await fetch(`${API_BASE_URL}/tours?${searchParams.toString()}`, {
    method: 'GET',
    headers: getApiHeaders('json'),
  });

  if (!response.ok) throw await buildError(response, 'Failed to fetch tours');

  const data = await response.json();
  const rawItems = Array.isArray(data?.items) ? data.items : [];
  const paginationSource = data?.pagination || {};

  const result: ActivityListResponse = {
    items: rawItems.map(normalizeSummary).filter((item: ActivitySummary) => item.id > 0),
    pagination: {
      page: toNumber(paginationSource.page ?? data?.page, 1),
      limit: toNumber(paginationSource.limit ?? data?.limit ?? data?.pageSize, 50),
      total: toNumber(paginationSource.total ?? data?.total, rawItems.length),
      totalPages: toNumber(paginationSource.totalPages ?? data?.totalPages, 1),
    },
  };

  const priceJobId = data?.priceJobId ?? data?.price_job_id;
  if (priceJobId) result.priceJobId = String(priceJobId);

  return result;
};

export const fetchTourPrices = async (priceJobId: string): Promise<ActivityPricePollResponse> => {
  const response = await fetch(`${API_BASE_URL}/tours/prices/${encodeURIComponent(priceJobId)}`, {
    method: 'GET',
    headers: getApiHeaders('json'),
  });

  if (!response.ok) throw await buildError(response, 'Failed to fetch tour prices');

  const data = await response.json();
  const status = String(data?.status || '').toUpperCase();

  if (status === 'PENDING') return { status: 'PENDING' };

  if (status === 'SUCCESS') {
    const rawPrices = data?.prices && typeof data.prices === 'object' ? data.prices : {};
    const prices: Record<string, ActivityPrice> = {};
    Object.entries(rawPrices).forEach(([key, value]) => {
      prices[key] = normalizePrice(value);
    });
    return {
      status: 'SUCCESS',
      currency: 'INR',
      prices,
    };
  }

  return {
    status: 'FAILED',
    error: data?.error ? String(data.error) : undefined,
  };
};

export const fetchTourCities = async (countryId?: number): Promise<ActivityCity[]> => {
  const searchParams = new URLSearchParams();
  if (countryId !== undefined) searchParams.set('countryId', String(countryId));
  const suffix = searchParams.toString();

  const response = await fetch(`${API_BASE_URL}/tours/cities${suffix ? `?${suffix}` : ''}`, {
    method: 'GET',
    headers: getApiHeaders('json'),
  });

  if (!response.ok) throw await buildError(response, 'Failed to fetch tour cities');

  const data = await response.json();
  const items = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
  return items.map(normalizeCity).filter((item: ActivityCity) => item.cityId > 0);
};
